/**
 * The admin side of support calls, mounted ONCE in the shell so an incoming call rings on
 * every screen, not only while the inbox is open.
 *
 * The engine is `@most/webrtc-call`; frames reach it through `useWsEvent`, which requires
 * the WsProvider above (App.tsx LiveGate). Signaling bodies never ride the socket — the
 * engine drains them over REST after each thin nudge, and polls as a fallback.
 */
import { createContext, useContext, useEffect, useRef, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Phone, PhoneIncoming, PhoneOff, Video } from 'lucide-react';
import { useWsEvent } from '@most/ws-client';
import { Button, cn } from '@most/ui';
import { useSupportCall, type CallApi } from '@most/webrtc-call';
import * as API from '@/lib/api';
import { qk, useChats } from '@/lib/queries';

const callApi: CallApi = {
  start: (kind, conversationId) => API.startChatCall(kind, conversationId),
  get: API.getChatCall,
  accept: API.acceptChatCall,
  decline: API.declineChatCall,
  end: API.endChatCall,
  postSignal: async (id, data) => {
    await API.postChatCallSignal(id, data);
  },
  getSignals: async (id) => (await API.getChatCallSignals(id)).signals,
};

type AdminCall = ReturnType<typeof useSupportCall>;

const Ctx = createContext<AdminCall | null>(null);

export function useAdminCall(): AdminCall {
  const value = useContext(Ctx);
  if (!value) throw new Error('useAdminCall must be used inside <AdminCallProvider>');
  return value;
}

export function AdminCallProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();

  const call = useSupportCall({
    api: callApi,
    side: 'admin',
    onTerminal: () => {
      // A terminal call leaves a log line in the thread.
      void qc.invalidateQueries({ queryKey: qk.chats });
      void qc.invalidateQueries({ queryKey: ['admin', 'chat'] });
    },
  });

  useWsEvent('call.state', (e) => {
    if (typeof e.id === 'string') call.onCallStateFrame(e.id);
  });
  useWsEvent('call.signal', (e) => {
    if (typeof e.id === 'string') call.onCallSignalFrame(e.id);
  });

  return (
    <Ctx.Provider value={call}>
      {children}
      <AdminCallOverlay call={call} />
    </Ctx.Provider>
  );
}

function AdminCallOverlay({ call }: { call: AdminCall }) {
  const { t } = useTranslation();
  const { data: chats } = useChats();
  const remoteRef = useRef<HTMLVideoElement>(null);
  const localRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    if (remoteRef.current) remoteRef.current.srcObject = call.remoteStream;
  }, [call.remoteStream, call.phase]);
  useEffect(() => {
    if (localRef.current) localRef.current.srcObject = call.localStream;
  }, [call.localStream, call.phase]);

  if (call.phase === 'idle') return null;

  // The admin may see who is on the line — resolved from the inbox, never from the call
  // DTO itself (which is side-based and person-free for the user's benefit).
  const thread = chats?.items.find((c) => c.id === call.call?.conversation_id);
  const who = thread?.display_name || thread?.user_email || '';

  const isVideo = call.call?.kind === 'video';
  const statusKey =
    call.phase === 'outgoing'
      ? 'inbox.call.outgoing'
      : call.phase === 'incoming'
        ? 'inbox.call.incoming'
        : call.phase === 'connecting'
          ? 'inbox.call.connecting'
          : call.phase === 'active'
            ? 'inbox.call.active'
            : 'inbox.call.endedStatus';

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/80 p-4">
      <div className="flex w-full max-w-md flex-col items-center gap-4 rounded bg-rail p-6 text-white shadow-overlay">
        <p className="flex items-center gap-2 text-sm text-white/70">
          {call.phase === 'incoming' ? (
            <PhoneIncoming className="h-4 w-4 animate-pulse text-green-400" />
          ) : isVideo ? (
            <Video className="h-4 w-4" />
          ) : (
            <Phone className="h-4 w-4" />
          )}
          {t(statusKey)}
        </p>
        {who && <p className="text-base font-semibold">{who}</p>}

        <div className="relative w-full">
          <video
            ref={remoteRef}
            autoPlay
            playsInline
            className={cn('w-full rounded bg-black', isVideo ? 'aspect-video' : 'h-24 opacity-40')}
          />
          {isVideo && (
            <video
              ref={localRef}
              autoPlay
              playsInline
              muted
              className="absolute bottom-2 right-2 w-24 rounded border border-white/30 bg-black"
            />
          )}
        </div>

        {call.error && (
          <p className="text-xs text-red-400">
            {t(
              call.error === 'media_denied'
                ? 'inbox.call.mediaDenied'
                : call.error === 'connection_lost'
                  ? 'inbox.call.connectionLost'
                  : 'inbox.call.failed',
            )}
          </p>
        )}

        <div className="flex items-center gap-3">
          {call.phase === 'incoming' ? (
            <>
              <Button className="bg-green-600 text-white hover:bg-green-500" onClick={() => void call.accept()}>
                <Phone className="mr-2 h-4 w-4" /> {t('inbox.call.accept')}
              </Button>
              <Button variant="destructive" onClick={() => void call.decline()}>
                <PhoneOff className="mr-2 h-4 w-4" /> {t('inbox.call.decline')}
              </Button>
            </>
          ) : (
            <Button variant="destructive" onClick={() => void call.hangUp()}>
              <PhoneOff className="mr-2 h-4 w-4" /> {t('inbox.call.hangUp')}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
